import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FileText,
  Hash,
  Layers,
  Loader2,
  MapPin,
  Maximize2,
  Minimize2,
  RefreshCw,
  Table2,
  Tag,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import PdfPane from "../components/PdfPane";
import StatusBadge from "../components/StatusBadge";
import { deliverableFileUrl, listDeliverables, parseDeliverable } from "./api";
import { ExtractedEntity, ProjectDeliverable, VerityProject } from "./types";

/**
 * Deliverables already downloaded, kept for the length of the session.
 *
 * A DRAWING PACK IS BIG AND IT DOES NOT CHANGE. A deliverable's file is written once at upload and
 * never edited — a revision is a new deliverable with a new id — so a cached copy can never be
 * stale, and the id alone is a sound key. Re-parsing re-reads the file on the server and leaves the
 * bytes alone, so it does not invalidate anything here either.
 *
 * SMALL AND CAPPED. Three packs at 35 MB is over a hundred megabytes of browser memory, which is
 * enough; past that the least recently used one is dropped. Module-level rather than state, because
 * the point is to survive this screen being unmounted — which is exactly what happens when somebody
 * steps back to Deliverables and returns.
 */
const FILE_CACHE = new Map<string, File>();
const FILE_CACHE_LIMIT = 3;

function cacheGet(id: string): File | undefined {
  const hit = FILE_CACHE.get(id);
  // Re-insert so the most recently used is last, which is what makes the eviction below an LRU
  // rather than "whichever happened to arrive first".
  if (hit) {
    FILE_CACHE.delete(id);
    FILE_CACHE.set(id, hit);
  }
  return hit;
}

function cachePut(id: string, file: File): void {
  FILE_CACHE.delete(id);
  FILE_CACHE.set(id, file);
  while (FILE_CACHE.size > FILE_CACHE_LIMIT) {
    const oldest = FILE_CACHE.keys().next().value;
    if (oldest === undefined) break;
    FILE_CACHE.delete(oldest);
  }
}

/**
 * Reads a response body, reporting how much of it has arrived.
 *
 * MEASURED, NOT ESTIMATED. A guess from "35 MB at some assumed bandwidth" is wrong on every network
 * it meets and wrong in a way the reader can see, because the bar and the wait disagree. The route
 * sets `Content-Length` and there is no compression middleware in front of it, so the bytes read are
 * directly comparable to the bytes promised and the percentage is the real one.
 *
 * Falls back to a plain `blob()` where the browser gives no readable stream or the server sends no
 * length — an unknown total is a spinner's job, and a bar with an invented denominator is worse than
 * no bar.
 */
async function readWithProgress(
  res: Response,
  knownSize: number,
  onProgress: (fraction: number) => void,
): Promise<Blob> {
  // The header first, the stored size second. THE RECORD IS THE RELIABLE ONE: the deliverable knows
  // exactly how many bytes were uploaded, while `Content-Length` can go missing on the way — a proxy
  // switching to chunked encoding is enough, and then a header-only version silently falls back to
  // a bare spinner with no way to tell from the outside why.
  const fromHeader = Number(res.headers.get("Content-Length") ?? 0);
  const total = Number.isFinite(fromHeader) && fromHeader > 0 ? fromHeader : knownSize;
  const reader = res.body?.getReader();
  if (!reader || !Number.isFinite(total) || total <= 0) return res.blob();

  const chunks: Uint8Array[] = [];
  let loaded = 0;
  let lastReported = -1;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    chunks.push(value);
    loaded += value.length;

    // A 34 MB download arrives in roughly five hundred chunks. Reporting each one would re-render
    // the screen five hundred times to move a bar by a fifth of a percent.
    const whole = Math.min(99, Math.floor((loaded / total) * 100));
    if (whole !== lastReported) {
      lastReported = whole;
      onProgress(whole / 100);
    }
  }

  return new Blob(chunks as BlobPart[], { type: "application/pdf" });
}

interface DocumentAIProps {
  project: VerityProject;
  /** The deliverable the ⚡ was pressed on, pre-selected when arriving from Deliverables. */
  focusId?: string | null;
  /** Watching, not working — no re-parse. See ProjectDeliverables. */
  readOnly?: boolean;
}

/**
 * Document AI — what the reader made of a parsed deliverable.
 *
 * Left is the document: the parsed deliverables to pick from, the one in hand, and the values found
 * in it as chips. Right is the reading: the sections it was broken into, and every entity pulled out
 * with its provenance and confidence.
 *
 * Everything shown here was read out of the submitted file, and the left pane is now the file
 * itself. Clicking an entity turns the page it was read from and marks it — the reader already knew
 * which pages named it and was discarding the numbers on the way out, so the jump costs nothing to
 * know. What it marks is found in the browser rather than stored: see PdfPane's annotation search,
 * and note that a CAD sheet's box is the annotation's, which is a notes block rather than a word.
 *
 * The panel used to draw highlighted regions generated from a hash of the file name. Those marked
 * nothing, and were deleted. These are real or they are absent.
 */
const DocumentAI = ({ project, focusId, readOnly = false }: DocumentAIProps) => {
  const [rows, setRows] = useState<ProjectDeliverable[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(focusId ?? null);
  const [reparsing, setReparsing] = useState(false);

  /** The document in the left pane, and whether it is still on the wire. */
  const [file, setFile] = useState<File | null>(null);
  const [fetching, setFetching] = useState(false);
  /** How much of the document has arrived, 0-1. Null when there is no measurable total. */
  const [progress, setProgress] = useState<number | null>(null);

  /** The document filling the screen, with the sidebar and the step tracker behind it. */
  const [full, setFull] = useState(false);

  // Escape is what everybody tries first, and a view that covers the exit route without answering
  // to it is a trap.
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFull(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);

  /**
   * Where the pane is looking.
   *
   * `key` exists so that clicking the same entity twice jumps again: the page and the quote are
   * unchanged, so nothing else about this object would tell the pane to move.
   */
  const [jump, setJump] = useState<{ page: number; quote?: string; key: number }>({
    page: 1,
    key: 0,
  });

  const load = useCallback(async () => {
    try {
      setError(null);
      setRows(await listDeliverables(project.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the deliverables.");
      setRows([]);
    }
  }, [project.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Only parsed deliverables belong here — an unparsed file has nothing for this screen to show.
  const parsed = useMemo(() => (rows ?? []).filter((r) => r.extraction), [rows]);

  const selected = useMemo(
    () => parsed.find((r) => r.id === selectedId) ?? parsed[0],
    [parsed, selectedId],
  );

  const extraction = selected?.extraction;

  /**
   * Fetch the document whenever the picked one changes.
   *
   * Fetched to a blob rather than pointed at with an `<iframe src>`: the file route runs behind the
   * same auth as everything else, and a frame would send an unauthenticated request. Same reason the
   * findings export fetches rather than links.
   */
  useEffect(() => {
    if (!selected) {
      setFile(null);
      return;
    }

    setJump({ page: 1, key: 0 });

    // ALREADY IN HAND. Stepping out to Deliverables and back, or switching between two documents
    // and back again, used to pay the full download every time — thirty-five megabytes to show a
    // sheet the browser had already had. From the cache it is immediate, with no loading state at
    // all, because there is nothing to wait for.
    const cached = cacheGet(selected.id);
    if (cached) {
      setFetching(false);
      setFile(cached);
      return;
    }

    let cancelled = false;
    setFetching(true);
    setProgress(null);
    setFile(null);

    void (async () => {
      try {
        const res = await fetch(deliverableFileUrl(project.id, selected.id), {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Could not open the document");

        const blob = await readWithProgress(res, selected.fileSize, (fraction) => {
          if (!cancelled) setProgress(fraction);
        });

        const held = new File([blob], selected.fileName, { type: "application/pdf" });
        // Cached even if this screen has moved on — the work is done, and the next visit is the one
        // that benefits.
        cachePut(selected.id, held);
        if (!cancelled) setFile(held);
      } catch {
        if (!cancelled) setError("The source document could not be opened.");
      } finally {
        if (!cancelled) {
          setFetching(false);
          setProgress(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [project.id, selected?.id, selected?.fileName]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Send the pane to where an entity was read from.
   *
   * An entity can name several pages — "OPSD" is on half the pack. The first is the one to open;
   * the rest are what the source line already lists for anybody who wants them.
   */
  const goTo = (entity: ExtractedEntity) => {
    const page = entity.pages?.[0];
    if (!page) return;
    setJump((prev) => ({ page, quote: entity.value, key: prev.key + 1 }));
  };

  /** Re-runs Document AI over the document in hand — the only way to refresh an older parse. */
  const reparse = async () => {
    if (!selected || reparsing) return;
    setReparsing(true);
    setError(null);
    try {
      const updated = await parseDeliverable(project.id, selected.id);
      setRows((prev) => (prev ?? []).map((r) => (r.id === updated.id ? updated : r)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "The parse failed.");
    } finally {
      setReparsing(false);
    }
  };

  if (rows === null) {
    return (
      <div className="vr-empty">
        <Loader2 size={16} className="vr-spin-inline" /> Loading…
      </div>
    );
  }

  if (parsed.length === 0) {
    return (
      <GlowCard hover={false}>
        <div className="vr-soon">
          <div className="vr-soon-icon">
            <FileText size={28} />
          </div>
          <h2 className="vr-h-sec">Nothing parsed yet</h2>
          <p className="vr-muted" style={{ maxWidth: 460 }}>
            {rows.length === 0
              ? "This project has no deliverables. Add them on the Deliverables step first."
              : "Go back to Deliverables and press the ⚡ on a document to parse it. It will appear here as a source document."}
          </p>
        </div>
      </GlowCard>
    );
  }

  const readability = selected?.drawingSummary?.readability;

  return (
    // ONE TREE, TWO SHAPES. Full screen is a class on this wrapper rather than a different element
    // returned from a branch: returning a different tree would unmount the PDF pane, and it would
    // re-open and re-parse 35 MB every time somebody expanded or collapsed the view. Out of full
    // screen the wrapper is `display: contents`, so the layout above it is untouched.
    <div className={`vr-docai-shell${full ? " vr-docai-fullscreen" : ""}`}>
      {error && <div className="vr-form-error">{error}</div>}

      {/* A PACK NOBODY CAN READ MUST SAY SO HERE, not in a compliance result that looks clean.
          A scanned submission has sheets, has pages and has a header on every one, so nothing about
          it reads as broken — and a 743-rule check against it comes back almost empty and calls
          itself finished. This is the one screen where somebody is looking at what was read. */}
      {readability && readability.verdict !== "ok" && (
        <div className={readability.verdict === "unreadable" ? "vr-form-error" : "vr-readability-warn"}>
          <strong>
            {readability.verdict === "unreadable"
              ? "There is almost no text in this document."
              : "There is very little text in this document."}
          </strong>{" "}
          {readability.readableSheets} of{" "}
          {(selected?.extraction?.sections?.length ?? 0) || readability.readableSheets} sheets carry
          readable text, averaging {readability.charsPerSheet.toLocaleString()} characters each.
          <ul className="vr-readability-list">
            {readability.issues.map((i) => (
              <li key={i.code}>{i.detail}</li>
            ))}
          </ul>
          A compliance check reads the drawings' words. Against this pack it will find almost nothing
          and that result will not mean the submission is compliant.
        </div>
      )}

      <div className="vr-docai">
        <GlowCard className="vr-docai-source" hover={false}>
          <div className="vr-card-head">
            <Layers size={14} />
            <h3>Source Document Preview</h3>
            {/* Labelled both ways. An icon alone is a guess until you press it, and the two states
                are the same control, so they should look like the same control. */}
            <button
              className="vr-btn vr-btn-sm vr-btn-outline"
              onClick={() => setFull((open) => !open)}
              disabled={!full && !selected}
              style={{ marginLeft: "auto" }}
            >
              {full ? (
                <>
                  <Minimize2 size={13} /> Exit Full Screen
                </>
              ) : (
                <>
                  <Maximize2 size={13} /> Full Screen
                </>
              )}
            </button>
            {!readOnly && (
              <button
                className="vr-rowbtn vr-icononly"
                onClick={() => void reparse()}
                disabled={!selected || reparsing}
                title="Re-run Document AI over this document"
                aria-label="Re-parse"
              >
                {reparsing ? (
                  <Loader2 size={13} className="vr-spin-inline" />
                ) : (
                  <RefreshCw size={13} />
                )}
              </button>
            )}
          </div>

          {/* Click a document to read it. The list is the picker; the panel below is the document. */}
          <div className="vr-doc-picker">
            {parsed.map((row) => (
              <button
                key={row.id}
                type="button"
                className={`vr-doc-pick${row.id === selected?.id ? " vr-active" : ""}`}
                onClick={() => setSelectedId(row.id)}
                title={row.fileName}
              >
                <FileText size={13} />
                <span>{row.fileName}</span>
              </button>
            ))}
          </div>

          {selected && (
            <div className="vr-doc-preview">
              <PdfPane
                file={file}
                loading={fetching}
                // Not the file name: the picker above the pane already says which deliverable is
                // open, and a pack can hold several.
                loadingLabel="Fetching deliverable…"
                loadingProgress={progress}
                page={jump.page}
                quote={jump.quote}
                jumpKey={jump.key}
                // A drawing pack keeps most of its text in annotations rather than in the text
                // layer, so without this the highlight would never appear on a CAD sheet.
                searchAnnotations
                // Thirty sheets is a document somebody looks through, unlike a 272-page standard.
                continuous
              />
            </div>
          )}

        </GlowCard>

        <div className="vr-docai-read">
          <GlowCard className="vr-table-card" hover={false}>
            <div className="vr-table-head">
              <div className="vr-card-head">
                <Table2 size={14} />
                <h3>Parsed Sections</h3>
              </div>
            </div>
            <div className="vr-section-list">
              {(extraction?.sections ?? []).map((section) => (
                <div key={section.name} className="vr-section-row">
                  <span className="vr-section-name">{section.name}</span>
                  <span className="vr-cell-code">{section.fields} fields</span>
                  <StatusBadge
                    status={section.status}
                    label={section.status === "pass" ? "Pass" : section.status === "warning" ? "Warning" : "Fail"}
                  />
                </div>
              ))}
            </div>
          </GlowCard>

          <GlowCard className="vr-table-card" hover={false}>
            <div className="vr-table-head">
              <div className="vr-card-head">
                <Tag size={14} />
                <h3>Extracted Entities ({extraction?.entities.length ?? 0})</h3>
              </div>
            </div>
            <div className="vr-entity-list">
              {(extraction?.entities ?? []).map((entity) => {
                // An entity parsed before pages were recorded has nowhere to send the pane, and a
                // button that does nothing when pressed is worse than a line of text.
                const canJump = (entity.pages?.length ?? 0) > 0 && !!file;
                return (
                <div
                  key={`${entity.type}-${entity.value}`}
                  className={`vr-entity-row${canJump ? " vr-entity-jump" : ""}`}
                  role={canJump ? "button" : undefined}
                  tabIndex={canJump ? 0 : undefined}
                  onClick={canJump ? () => goTo(entity) : undefined}
                  onKeyDown={
                    canJump
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            goTo(entity);
                          }
                        }
                      : undefined
                  }
                  title={canJump ? `Show this on page ${entity.pages![0]}` : undefined}
                >
                  {canJump ? <MapPin size={12} /> : <Hash size={12} />}
                  <div className="vr-entity-body">
                    <span className="vr-entity-value">{entity.value}</span>
                    <span className="vr-entity-source">
                      {entity.type} — {entity.source}
                    </span>
                  </div>
                  {/* Dimmed below 90: a value the reader is unsure of should look unsure. */}
                  <span
                    className={`vr-entity-conf${entity.confidence < 90 ? " vr-low" : ""}`}
                    title={`${entity.confidence}% confidence`}
                  >
                    {entity.confidence}%
                  </span>
                </div>
                );
              })}
            </div>
          </GlowCard>
        </div>
      </div>
    </div>
  );
};

export default DocumentAI;
